import { useEffect, useMemo, useState } from 'react';
import { useHttpGetHolidaysByBranchOnMount, useHttpGetPoliciesByOrganizationOnMount, useHttpGetUsers, useOrganizationId } from '../../hooks';
import { Button, Checkbox, SearchableDropdown, DatePickerInputFormik, IMenuItem } from '../Common';
import { useTranslation } from '../../hooks/useTranslation';
import { useFormik } from 'formik';
import { DateTime } from 'luxon';
import { convertIsoToDiffDaysWithoutWeekendsAndHolidays } from '../../util/date/dateHelper';
import { LeaveRequestPDF } from '../RequestListPdfGenerate';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { AuthState } from '../../store/slices/auth.slice';
import { HolidayDto } from '../../commonTypes/organization.types';
import Papa from 'papaparse';
import saveAs from 'file-saver';

interface ExportProps {
    hideUserSelection?: boolean;
    setSelectedMember?: (item: IMenuItem) => void;
    setSelectedPolicy?: (item: IMenuItem) => void;
    setFilterDates?: (dateFrom: Date | null, dateTo: Date | null) => void;
    filteredRequests?: Array<any>;
    onCancel: () => void;
    userInfo?: string;
}

export const Export = ({
    hideUserSelection,
    setSelectedMember,
    setSelectedPolicy,
    setFilterDates,
    filteredRequests = [],
    onCancel,
    userInfo,
}: ExportProps) => {
    const organizationId = useOrganizationId();
    const { t } = useTranslation();
    const [isVisible, setIsVisible] = useState(true);

    const [dateFromObj, setDateFromObj] = useState<Date | null>(null);
    const [dateToObj, setDateToObj] = useState<Date | null>(null);
    const auth = useSelector<RootState, AuthState>(state => state.auth);
    const { data: holidays } = useHttpGetHolidaysByBranchOnMount(auth.value.branchId);

    const { data: members } = useHttpGetUsers(organizationId);
    const [menuItems, setMenuItems] = useState<IMenuItem[]>([]);
    const totalDays = useMemo(() => {
        return filteredRequests.reduce((acc, req) => {
            return acc + convertIsoToDiffDaysWithoutWeekendsAndHolidays(req.fromDate, req.toDate, holidays, req.leaveDurationType);
        }, 0);
    }, [filteredRequests]);

    const context = useFormik({
        initialValues: {
            dateFrom: '',
            dateTo: '',
        },
        onSubmit: values => {
            if (setFilterDates) {
                const fromDate = DateTime.fromFormat(values.dateFrom, 'dd/MM/yyyy').toJSDate();
                const toDate = DateTime.fromFormat(values.dateTo, 'dd/MM/yyyy').toJSDate();
                setFilterDates(fromDate, toDate);
            }
        },
    });
    useEffect(() => {
        const filteredMembers = members.map(member => ({
            label: `${member.givenName} ${member.familyName}`,
            value: member.userSub,
        }));
        setMenuItems(filteredMembers);
    }, [members]);

    const handleDropdownChange = (item: IMenuItem) => {
        if (setSelectedMember) {
            setSelectedMember(item);
        }
    };

    const { policies } = useHttpGetPoliciesByOrganizationOnMount();

    const policyOptions: IMenuItem[] = policies.map(policy => ({
        label: policy.leaveType,
        value: policy.id,
    }));

    const [isCurrentYearSelected, setIsCurrentYearSelected] = useState(false);
    const [isLastYearSelected, setIsLastYearSelected] = useState(false);

    const handleDateChange = async (date: DateTime | null, fieldName: 'dateFrom' | 'dateTo') => {
        if (date && date.isValid) {
            const jsDate = date.toJSDate();
            const formattedDate = date.toFormat('dd/MM/yyyy');

            if (fieldName === 'dateFrom') {
                const dateTo = context.values.dateTo ? DateTime.fromFormat(context.values.dateTo, 'dd/MM/yyyy') : null;

                if (dateTo && date > dateTo) {
                    await context.setFieldValue(fieldName, 'Invalid date');
                    return;
                }
                setDateFromObj(jsDate);
            } else if (fieldName === 'dateTo') {
                const dateFrom = context.values.dateFrom ? DateTime.fromFormat(context.values.dateFrom, 'dd/MM/yyyy') : null;

                if (dateFrom && date < dateFrom) {
                    await context.setFieldValue(fieldName, 'Invalid date');
                    return;
                }
                setDateToObj(jsDate);
            }

            await context.setFieldValue(fieldName, formattedDate);

            if (setFilterDates) {
                setFilterDates(fieldName === 'dateFrom' ? jsDate : dateFromObj, fieldName === 'dateTo' ? jsDate : dateToObj);
            }
        }
    };

    const handleCheckboxChange = async (e: React.ChangeEvent<HTMLInputElement>, year: 'current' | 'last') => {
        const currentDate = DateTime.now();
        const currentYear = currentDate.year;

        if (year === 'current') {
            setIsCurrentYearSelected(e.target.checked);
            if (e.target.checked) {
                setIsLastYearSelected(false);
                const startDate = DateTime.fromObject({ year: currentYear, month: 1, day: 1 });
                const endDate = DateTime.fromObject({ year: currentYear, month: 12, day: 31 });

                if (setFilterDates) {
                    setFilterDates(startDate.toJSDate(), endDate.toJSDate());
                }
            }
        }
        if (year === 'last') {
            setIsLastYearSelected(e.target.checked);
            if (e.target.checked) {
                setIsCurrentYearSelected(false);
                const startDate = DateTime.fromObject({ year: currentYear - 1, month: 1, day: 1 });
                const endDate = DateTime.fromObject({ year: currentYear - 1, month: 12, day: 31 });

                if (setFilterDates) {
                    setFilterDates(startDate.toJSDate(), endDate.toJSDate());
                }
            }
        }

        if (!e.target.checked && !isCurrentYearSelected && !isLastYearSelected) {
            const today = DateTime.now();

            setDateFromObj(today.toJSDate());
            setDateToObj(today.toJSDate());

            await context.setFieldValue('dateFrom', today.toFormat('dd/MM/yyyy'));
            await context.setFieldValue('dateTo', today.toFormat('dd/MM/yyyy'));

            if (setFilterDates) {
                setFilterDates(today.toJSDate(), today.toJSDate());
            }
        }
    };
    const getPeriod = () => {
        const currentDate = DateTime.now();
        const currentYear = currentDate.year;
        const lastYear = currentYear - 1;

        if (isCurrentYearSelected) {
            const startDate = DateTime.fromObject({ year: currentYear, month: 1, day: 1 });
            const endDate = DateTime.fromObject({ year: currentYear, month: 12, day: 31 });
            return `${startDate.toFormat('dd.MM.yyyy')} - ${endDate.toFormat('dd.MM.yyyy')}`;
        }

        if (isLastYearSelected) {
            const startDate = DateTime.fromObject({ year: lastYear, month: 1, day: 1 });
            const endDate = DateTime.fromObject({ year: lastYear, month: 12, day: 31 });
            return `${startDate.toFormat('dd.MM.yyyy')} - ${endDate.toFormat('dd.MM.yyyy')}`;
        }

        if (dateFromObj && dateToObj) {
            return `${DateTime.fromJSDate(dateFromObj).toFormat('dd.MM.yyyy')} - ${DateTime.fromJSDate(dateToObj).toFormat('dd.MM.yyyy')}`;
        }

        return '';
    };

    const handlePolicyChange = (item: IMenuItem) => {
        if (setSelectedPolicy) {
            setSelectedPolicy(item);
        }
    };

    const handleCancel = async () => {
        onCancel();
        setIsVisible(false);
    };

    const clearDateField = async (date: string) => {
        await context.setFieldValue(date, '');

        if (setFilterDates) {
            setFilterDates(null, null);
        }
    };

    return isVisible ? (
        <div className="export-container">
            {hideUserSelection && (
                <div className="export-container__row">
                    <p className="paragraph-1">{t('export.export')}</p>
                </div>
            )}
            <div className="export-container__row">
                {!hideUserSelection && (
                    <div className="input-container">
                        <SearchableDropdown
                            label={t('export.selectUser')}
                            menuItems={menuItems}
                            onChange={handleDropdownChange}
                            placeholder={t('common.search')}
                        />
                    </div>
                )}

                <div className="input-container">
                    <SearchableDropdown
                        label={t('export.selectPolicy')}
                        menuItems={policyOptions}
                        onChange={handlePolicyChange}
                        placeholder={t('export.selectPolicy')}
                    />
                </div>
            </div>
            <div className="export-container__row">
                {!isCurrentYearSelected && !isLastYearSelected && (
                    <>
                        <div className="input-container">
                            <DatePickerInputFormik
                                context={context}
                                id="dateFrom"
                                label={t('export.dateFrom')}
                                date={new Date() || dateFromObj}
                                placeholder="DD/MM/YYYY"
                                changeDate={date => handleDateChange(date, 'dateFrom')}
                                onPeriodChange={() => {}}
                                clearData={() => clearDateField('dateFrom')}
                            />
                        </div>
                        <div className="input-container">
                            <DatePickerInputFormik
                                context={context}
                                id="dateTo"
                                label={t('export.dateTo')}
                                date={new Date() || dateToObj}
                                placeholder="DD/MM/YYYY"
                                changeDate={date => handleDateChange(date, 'dateTo')}
                                onPeriodChange={() => {}}
                                clearData={() => clearDateField('dateTo')}
                            />
                        </div>
                    </>
                )}
            </div>

            <div className="export-container__row-period">
                <p className="paragraph-3">{t('export.period')}</p>
                <div className="export-container__rows-period">
                    <Checkbox
                        id={'currentYear'}
                        label={t('export.currentYear')}
                        checked={isCurrentYearSelected}
                        handleChange={e => handleCheckboxChange(e, 'current')}
                        labelClassName="paragraph-3"
                    />
                    <Checkbox
                        id={'lastYear'}
                        label={t('export.lastYear')}
                        checked={isLastYearSelected}
                        handleChange={e => handleCheckboxChange(e, 'last')}
                        labelClassName="paragraph-3"
                    />
                </div>
            </div>
            <div className="export-container__row-period">
                <p className="paragraph-1">{t('export.selected')}</p>
                <div className="export-container__rows-selected">
                    <div className="export-container__row-selected">
                        <p className="paragraph-3">{t('export.requests')}</p>
                        <p className="paragraph-3 export-container__color">{filteredRequests.length}</p>
                    </div>
                    <div className="export-container__row-selected">
                        <p className="paragraph-3">{t('export.totalDays')}</p>
                        <p className="paragraph-3 export-container__color">{totalDays}</p>
                    </div>
                </div>
            </div>
            <div className="export-container__row">
                <ExportDropdownButton
                    filteredRequests={filteredRequests}
                    totalDays={totalDays}
                    holidays={holidays}
                    period={getPeriod()}
                    userInfo={userInfo}
                ></ExportDropdownButton>
                <Button text={t('export.cancel')} variant="text" onClick={handleCancel} color="blue"></Button>
            </div>
        </div>
    ) : null;
};

interface IExportButton {
    filteredRequests: Array<any>;
    totalDays: number;
    holidays: HolidayDto[];
    period: string;
    userInfo: string | undefined;
}
const ExportDropdownButton = ({ filteredRequests, totalDays, holidays, period, userInfo }: IExportButton) => {
    const { t } = useTranslation();
    const [showDropdown, setShowDropdown] = useState(false);

    const exportToCSV = () => {
        const exportData = filteredRequests.map(req => {
            const userName = req.givenName && req.familyName ? `${req.givenName} ${req.familyName}`.trim() : userInfo ?? 'N/A';
            return {
                User: userName,
                Policy: req.policyType || 'N/A',
                'From Date': new Date(req.fromDate).toLocaleDateString(),
                'To Date': new Date(req.toDate).toLocaleDateString(),
                Days: convertIsoToDiffDaysWithoutWeekendsAndHolidays(req.fromDate, req.toDate, holidays, req.leaveDurationType),
                Status: req.status || 'N/A',
            };
        });
        const csv = Papa.unparse(exportData);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        saveAs(blob, `LeaveRequests-${DateTime.now().toFormat('dd-MM-yyyy')}.csv`);
    };

    return (
        <div className={`dropdown-button-container ${showDropdown ? 'open' : ''}`}>
            <Button
                text="Export as"
                className={`dropdown-toggle ${showDropdown ? 'open' : ''}`}
                variant="contained"
                onClick={() => setShowDropdown(!showDropdown)}
                iconSrc="/img/arrow-down-white.svg"
            />
            {showDropdown && (
                <div className="dropdown-menu">
                    <PDFDownloadLink
                        document={
                            <LeaveRequestPDF
                                filteredRequests={filteredRequests}
                                totalDays={totalDays}
                                holidays={holidays}
                                period={period}
                                userInfo={userInfo}
                            />
                        }
                        fileName={`LeaveRequest-${DateTime.now().toFormat('dd-MM-yyyy')}.pdf`}
                    >
                        <div className="dropdown-item">{t('export.exportAsPDF')}</div>
                    </PDFDownloadLink>
                    <div className="dropdown-item" onClick={exportToCSV}>
                        {t('export.exportAsCSV')}
                    </div>
                </div>
            )}
        </div>
    );
};
